/**
 * Global confirmation modal — ESM port of the inline <script>
 * historically embedded in ``templates/office/components/_confirm_modal.html``.
 *
 * Public API (kept identical for back-compat — 12+ call sites in
 * ``frontend/src/pages/`` and several legacy templates already
 * use these globals; see ``.cursor/rules/confirm-modal.mdc``):
 *
 *   window.showConfirmModal({
 *       title:        string,                    // plain text
 *       message:      string,                    // plain text (textContent)
 *       messageHtml:  string,                    // OR rich HTML (innerHTML)
 *       confirmText:  string,                    // defaults to "Confirm"
 *       cancelText:   string,                    // defaults to "Cancel"
 *       type:         'danger'|'warning'|'success'|'info',  // default 'danger'
 *       inputLabel:   string,                    // shows reason textarea
 *       inputPlaceholder: string,
 *       onConfirm:    (reason?: string) => void  // reason = textarea value
 *   });
 *   window.hideConfirmModal();
 *
 * Defaults i18n
 * -------------
 * Default labels ("Are you sure?" / "Confirm" / "Cancel") used when the
 * caller omits the option are read from ``json_script#confirm-modal-i18n``
 * which the partial emits using Django ``{% trans %}``. The ESM module
 * therefore stays free of hardcoded English strings.
 *
 * DOM contract
 * ------------
 * The partial renders these IDs (do NOT rename without updating below):
 *   - #global-confirm-modal       outer overlay
 *   - #modal-title                <h3>
 *   - #modal-message              <p>
 *   - #modal-confirm-btn          confirm <button>
 *   - #modal-cancel-btn           cancel <button>
 *   - #modal-input-wrapper        textarea wrapper (hidden by default)
 *   - #modal-input-label          <label>
 *   - #modal-input                <textarea>
 *
 * Wired ONCE per SPA lifetime from ``app.js`` on ``DOMContentLoaded``.
 * Boosted hx-boost navigations do NOT re-render base_office.html so the
 * modal nodes survive every swap — re-binding on ``htmx:afterSwap`` would
 * just create duplicate listeners.
 */

const TYPE_BUTTON_CLASSES = {
  danger:
    'bg-red-600 hover:bg-red-700 focus:ring-red-300 dark:bg-red-500 dark:hover:bg-red-600 dark:focus:ring-red-900',
  warning:
    'bg-orange-600 hover:bg-orange-700 focus:ring-orange-300 dark:bg-orange-500 dark:hover:bg-orange-600 dark:focus:ring-orange-900',
  success:
    'bg-green-600 hover:bg-green-700 focus:ring-green-300 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800',
  info: 'btn-primary',
};

const CONFIRM_BTN_BASE_CLASS =
  'py-2.5 px-5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-4';

let _callback = null;
let _i18n = null;
let _bound = false;

function readI18n() {
  if (_i18n) return _i18n;
  const script = document.getElementById('confirm-modal-i18n');
  if (script && script.textContent) {
    try {
      _i18n = JSON.parse(script.textContent);
    } catch {
      _i18n = {};
    }
  } else {
    _i18n = {};
  }
  return _i18n;
}

function getModalRoot() {
  return document.getElementById('global-confirm-modal');
}

function showConfirmModal(options) {
  const opts = options || {};
  const modal = getModalRoot();
  if (!modal) return;

  const i18n = readI18n();
  const title = document.getElementById('modal-title');
  const message = document.getElementById('modal-message');
  const confirmBtn = document.getElementById('modal-confirm-btn');
  const cancelBtn = document.getElementById('modal-cancel-btn');
  const inputWrapper = document.getElementById('modal-input-wrapper');
  const inputLabel = document.getElementById('modal-input-label');
  const inputEl = document.getElementById('modal-input');

  title.textContent = opts.title || i18n.areYouSure || 'Are you sure?';

  // ``messageHtml`` opts into innerHTML; defaults to safe textContent.
  // See .cursor/rules/confirm-modal.mdc for the XSS contract.
  if (opts.messageHtml) {
    message.innerHTML = opts.messageHtml;
  } else {
    message.textContent = opts.message || '';
  }

  confirmBtn.textContent = opts.confirmText || i18n.confirm || 'Confirm';
  cancelBtn.textContent = opts.cancelText || i18n.cancel || 'Cancel';

  // Optional reason textarea. Off by default to keep the tight
  // "title + message" layout for the vast majority of callers.
  if (opts.inputLabel) {
    inputLabel.textContent = opts.inputLabel;
    inputEl.value = '';
    inputEl.placeholder = opts.inputPlaceholder || '';
    inputWrapper.classList.remove('hidden');
  } else {
    inputWrapper.classList.add('hidden');
    inputEl.value = '';
  }

  const type = opts.type || 'danger';
  const typeClass = TYPE_BUTTON_CLASSES[type] || TYPE_BUTTON_CLASSES.danger;
  confirmBtn.className = `${CONFIRM_BTN_BASE_CLASS} ${typeClass}`;

  _callback = opts.onConfirm;

  // The confirm button uses a single ``onclick`` slot (not addEventListener)
  // so successive opens always replace the previous handler — preventing
  // the historical "open modal, cancel, open again, click confirm → fires
  // twice" bug that delegated listeners would silently introduce.
  //
  // The reason textarea value is forwarded to onConfirm() ONLY when the
  // caller opted into ``inputLabel``. Callers that don't use it keep
  // their existing ``function(){...}`` zero-arg signatures.
  confirmBtn.onclick = function () {
    if (typeof _callback === 'function') {
      const val = opts.inputLabel ? inputEl.value || '' : undefined;
      _callback(val);
    }
    hideConfirmModal();
  };

  modal.classList.remove('hidden');
  modal.classList.add('flex');
  document.body.classList.add('overflow-hidden');
}

function hideConfirmModal() {
  const modal = getModalRoot();
  if (!modal) return;
  modal.classList.add('hidden');
  modal.classList.remove('flex');
  document.body.classList.remove('overflow-hidden');
  _callback = null;
}

/**
 * Wire the close affordances and the global Escape handler. Idempotent.
 * Called from ``app.js`` on ``DOMContentLoaded``.
 *
 * The partial declares the close affordances as ``[data-confirm-modal-close]``
 * (replaces the legacy ``onclick="hideConfirmModal()"`` markup). We bind
 * via delegation on the modal root so the cancel + the X button + any
 * future close-trigger inside the modal all flow through the same path.
 */
export function initConfirmModal() {
  if (_bound) return;
  const modal = getModalRoot();
  // Modal node may not exist on PWA / cleaner-mobile shells that don't
  // include the partial. Fail silently — callers using showConfirmModal
  // there would already be broken in the legacy code.
  if (!modal) return;
  _bound = true;

  modal.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-confirm-modal-close]');
    if (!trigger) return;
    event.preventDefault();
    hideConfirmModal();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const m = getModalRoot();
    if (m && !m.classList.contains('hidden')) {
      hideConfirmModal();
    }
  });

  // Public API — kept on window for back-compat with the 12+ call sites
  // in pages/ and inline templates that still call window.showConfirmModal.
  window.showConfirmModal = showConfirmModal;
  window.hideConfirmModal = hideConfirmModal;
}

export { showConfirmModal, hideConfirmModal };
